/**
 * Booking Window Resolver — pure, dependency-free lead-time gate shared by every
 * evaluator that decides whether a rule applies to a given date.
 *
 * Used by THREE call sites that must stay in sync:
 *   1. supabase/functions/pricing-engine  (authoritative — mirrored port)
 *   2. webHotelierPricingBridge           (preview/push)
 *   3. pricingRulesCalculatorService      (calendar chips display)
 *
 * Keep this file free of Supabase / network / Date.now() so it can be unit
 * tested and ported to the Deno edge function verbatim.
 *
 * Semantics:
 *   • The window is a ROLLING range measured in days from today, not a range of
 *     calendar dates. "4 to 8" means the rule fires on the dates that are 4–8
 *     days ahead of whenever the engine runs; tomorrow it is a different set of
 *     dates. Absolute calendar periods live in conditions.date_range and are
 *     ANDed with this gate by the callers.
 *   • Both ends are INCLUSIVE: 4–8 fires on day 4 and on day 8.
 *   • A missing min means 0 — "from today up to max", which is exactly the
 *     behaviour every rule had before min existed. This keeps every stored rule
 *     working untouched.
 *   • A missing max means no upper bound.
 *   • min > max matches NOTHING. Silently swapping them would price rooms off a
 *     range the operator never typed; refusing to fire is the recoverable
 *     failure. The UI flags this so the rule does not fail invisibly.
 */

// `| undefined` is explicit because the project runs exactOptionalPropertyTypes:
// callers build bounds from optional conditions and pass the undefined through.
export type BookingWindowBounds = {
  /** Earliest lead time, in days from today, at which the rule fires. */
  min?: number | null | undefined;
  /** Latest lead time, in days from today, at which the rule fires. */
  max?: number | null | undefined;
};

/** True when min/max are set the wrong way round — the rule can never fire. */
export function isInvertedWindow(bounds: BookingWindowBounds): boolean {
  const { min, max } = bounds;
  return min != null && max != null && min > max;
}

/**
 * Decide whether a date sitting `daysFromNow` days ahead falls inside the rule's
 * rolling booking window.
 */
export function isWithinBookingWindow(
  daysFromNow: number,
  bounds: BookingWindowBounds
): boolean {
  if (isInvertedWindow(bounds)) return false;

  const min = bounds.min ?? 0;
  if (daysFromNow < min) return false;

  if (bounds.max != null && daysFromNow > bounds.max) return false;

  return true;
}

/**
 * A rule as far as lead time is concerned. Two storage locations exist and both
 * must be read:
 *   • lead_time_min / lead_time_max — real columns on pricing_rules, what the
 *     booking-window UI writes and what the edge function already filters on.
 *   • conditions.booking_window_days — the legacy JSONB field. Rules saved
 *     before the UI moved to the columns still carry only this.
 */
export type LeadTimeRuleLike = {
  lead_time_min?: number | null | undefined;
  lead_time_max?: number | null | undefined;
  conditions?: Record<string, unknown> | undefined;
};

/**
 * Collapse both storage locations into the bounds the evaluators apply.
 *
 * Columns win over conditions, so re-saving a rule through the UI is what
 * migrates it. `fallbackMax` is the rule type's historical default (14 for
 * booking_window) and is applied ONLY when neither location holds a max —
 * applying it otherwise would cap a 20-30 day rule at 14 and silence it.
 */
export function resolveBookingWindowBounds(
  rule: LeadTimeRuleLike,
  fallbackMax?: number
): BookingWindowBounds {
  const legacyMax = rule.conditions?.['booking_window_days'];
  const legacyMin = rule.conditions?.['booking_window_days_min'];

  const min =
    rule.lead_time_min ??
    (typeof legacyMin === 'number' ? legacyMin : undefined);
  const max =
    rule.lead_time_max ??
    (typeof legacyMax === 'number' ? legacyMax : undefined) ??
    fallbackMax;

  return { min, max };
}

/** Human-readable window, for rule names, chips and "why not applied" reasons. */
export function describeBookingWindow(bounds: BookingWindowBounds): string {
  const { min, max } = bounds;
  if (isInvertedWindow(bounds)) return `${min}-${max}d (invalid range)`;
  if (min != null && min > 0 && max != null) return `${min}-${max}d out`;
  if (min != null && min > 0) return `${min}+d out`;
  if (max != null) return `within ${max}d`;
  return 'any lead time';
}
